/**
 * Schema Validator
 * Validate FormSchema structure and content
 */

import type { FormSchema, FormControl, FormMetadata } from '@atlas-forms/types-js';

/**
 * Validation result for schema validation
 */
export interface SchemaValidationResult {
  /** Is schema valid */
  isValid: boolean;

  /** Validation errors */
  errors: string[];

  /** Validation warnings */
  warnings: string[];
}

/**
 * Validate FormSchema structure and content
 * @param schema Schema to validate
 * @returns Validation result
 */
export function validateSchema(schema: FormSchema): SchemaValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate basic properties
  if (!schema.version) {
    errors.push('Schema must have version');
  }

  if (!schema.metadata) {
    errors.push('Schema must have metadata');
  } else {
    validateMetadata(schema.metadata, errors, warnings);
  }

  if (!schema.controls || schema.controls.length === 0) {
    warnings.push('Schema has no controls');
  } else {
    validateControls(schema.controls, errors, warnings);
  }

  // Validate sections if present
  if (schema.sections) {
    validateSections(schema.sections, errors, warnings);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate form metadata
 */
function validateMetadata(
  metadata: FormMetadata,
  errors: string[],
  warnings: string[]
): void {
  if (!metadata.formID) {
    errors.push('Metadata must have formID');
  }

  if (!metadata.title) {
    errors.push('Metadata must have title');
  }

  // Warnings
  if (!metadata.description) {
    warnings.push('Metadata should have description');
  }
}

/**
 * Validate controls array
 */
function validateControls(
  controls: FormControl[],
  errors: string[],
  warnings: string[]
): void {
  const ids = new Set<string>();

  controls.forEach((control, index) => {
    if (!control.id) {
      errors.push(`Control at index ${index} must have id`);
      return;
    }

    if (ids.has(control.id)) {
      errors.push(`Duplicate control id: ${control.id}`);
    }
    ids.add(control.id);

    if (!control.type) {
      errors.push(`Control ${control.id} must have type`);
    }

    if (!Number.isInteger(control.order)) {
      errors.push(`Control ${control.id} must have order number`);
    }

    // Validate field constraints
    if (control.validation) {
      validateFieldValidation(control, errors);
    }

    // Validate grid layout
    if (control.columnSpan) {
      if (control.columnSpan < 1 || control.columnSpan > 12) {
        errors.push(`Control ${control.id} columnSpan must be between 1 and 12`);
      }
    }

    if (control.gridPosition) {
      if (control.gridPosition.colSpan && (control.gridPosition.colSpan < 1 || control.gridPosition.colSpan > 12)) {
        errors.push(`Control ${control.id} gridPosition.colSpan must be between 1 and 12`);
      }
    }

    // Warnings
    if (!control.label && control.type !== 'custom') {
      warnings.push(`Control ${control.id} should have label`);
    }
  });

  // Check order is sequential
  const orders = controls.map((c) => c.order).sort((a, b) => a - b);
  if (orders[0] !== 0) {
    warnings.push('Control orders should start at 0');
  }
}

/**
 * Validate sections
 */
function validateSections(
  sections: unknown[],
  errors: string[],
  _warnings: string[]
): void {
  const ids = new Set<string>();

  sections.forEach((section: unknown, index: number) => {
    if (!section || typeof section !== 'object') {
      errors.push(`Section at index ${index} must be an object`);
      return;
    }

    const s = section as Record<string, unknown>;

    if (!s.id || typeof s.id !== 'string') {
      errors.push(`Section at index ${index} must have id`);
      return;
    }

    if (ids.has(s.id as string)) {
      errors.push(`Duplicate section id: ${s.id}`);
    }
    ids.add(s.id as string);

    if (!s.title) {
      errors.push(`Section ${s.id} must have title`);
    }

    if (!Number.isInteger(s.order)) {
      errors.push(`Section ${s.id} must have order number`);
    }
  });
}

/**
 * Validate field validation rules
 */
function validateFieldValidation(control: FormControl, errors: string[]): void {
  const validation = control.validation;
  if (!validation) {
    return;
  }

  if (validation.minLength && validation.maxLength) {
    if (validation.minLength > validation.maxLength) {
      errors.push(`Control ${control.id}: minLength > maxLength`);
    }
  }

  if (validation.min && validation.max) {
    if (validation.min > validation.max) {
      errors.push(`Control ${control.id}: min > max`);
    }
  }

  if (validation.pattern && typeof validation.pattern === 'string') {
    try {
      new RegExp(validation.pattern);
    } catch {
      errors.push(`Control ${control.id}: Invalid regex pattern: ${validation.pattern}`);
    }
  }
}
